import {
  ref,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from '../config/firebase.config';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  progress: number;
}

const DEFAULT_UPLOAD_TIMEOUT_MS = 45_000;

export class FirebaseStorageService {
  /**
   * Upload image with progress. Always settles (success, error, or timeout)
   * so pickup/delivery UI cannot stay stuck on "Uploading…".
   */
  async uploadImage(
    file: File,
    path: string,
    onProgress?: (progress: UploadProgress) => void,
    timeoutMs = DEFAULT_UPLOAD_TIMEOUT_MS
  ): Promise<string> {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        try {
          uploadTask.cancel();
        } catch {
          /* ignore */
        }
        reject(new Error(`Upload timed out after ${Math.round(timeoutMs / 1000)}s. Check connection and try again.`));
      }, timeoutMs);

      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        fn();
      };

      uploadTask.on(
        'state_changed',
        (snapshot: UploadTaskSnapshot) => {
          const progress = snapshot.totalBytes
            ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            : 0;
          onProgress?.({
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            progress,
          });
        },
        (error) => {
          finish(() => {
            console.error('Upload error:', error);
            reject(error);
          });
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            finish(() => resolve(downloadURL));
          } catch (e) {
            finish(() => reject(e));
          }
        }
      );
    });
  }

  async uploadProfilePhoto(userId: string, file: File): Promise<string> {
    const path = `riders/${userId}/profile/photo.jpg`;
    return this.uploadImage(file, path);
  }

  async uploadDeliveryPhoto(
    jobId: string,
    type: 'pickup' | 'delivery',
    file: File
  ): Promise<string> {
    const timestamp = Date.now();
    const path = `deliveries/${jobId}/${type}-${timestamp}.jpg`;
    return this.uploadImage(file, path);
  }

  async uploadDocument(userId: string, documentType: string, file: File): Promise<string> {
    const path = `riders/${userId}/documents/${documentType}.jpg`;
    return this.uploadImage(file, path);
  }

  async uploadSelfie(userId: string, file: File): Promise<string> {
    const path = `riders/${userId}/verification/selfie.jpg`;
    return this.uploadImage(file, path);
  }

  async uploadGearPhoto(
    userId: string,
    gearType: 'helmet' | 'vest',
    file: File
  ): Promise<string> {
    const date = new Date().toISOString().split('T')[0];
    const path = `riders/${userId}/gear/${date}-${gearType}.jpg`;
    return this.uploadImage(file, path);
  }

  async deleteFile(filePath: string): Promise<void> {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
  }

  async getFileURL(filePath: string): Promise<string> {
    const fileRef = ref(storage, filePath);
    return getDownloadURL(fileRef);
  }
}

export const storageService = new FirebaseStorageService();
